import React, { useEffect, useRef, useState } from 'react'

// Stays visible for at least this long, counting down via the progress bar.
// If the user hasn't clicked "Estou ciente" by the time it runs out, the
// toast auto-hides WITHOUT persisting the dismissal - so it still comes back
// next time the dashboard is loaded that same day (only an explicit click
// silences it for the day).
const ALERT_DURATION_MS = 30000
const STORAGE_KEY_PREFIX = 'franchise_events_alert_dismissed'

const todayKey = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const buildStorageKey = (companyId) => `${STORAGE_KEY_PREFIX}:${companyId}:${todayKey()}`

// The key bakes in today's local date, so a dismissal recorded today has no
// effect on tomorrow's key - the same login flow naturally re-evaluates and
// re-shows the alert the next day.
export const isFranchiseAlertDismissedToday = (companyId) => {
  try {
    return localStorage.getItem(buildStorageKey(companyId)) === '1'
  } catch {
    return false
  }
}

const FranchiseUsageAlert = ({ companyId, usage, onDismiss }) => {
  const [remainingPercent, setRemainingPercent] = useState(100)
  const startedAtRef = useRef(Date.now())

  useEffect(() => {
    startedAtRef.current = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current
      const remaining = Math.max(0, 100 - (elapsed / ALERT_DURATION_MS) * 100)
      setRemainingPercent(remaining)
      if (elapsed >= ALERT_DURATION_MS) {
        clearInterval(interval)
        onDismiss(false)
      }
    }, 100)
    return () => clearInterval(interval)
  }, [onDismiss])

  const handleAcknowledge = () => {
    try {
      localStorage.setItem(buildStorageKey(companyId), '1')
    } catch {
      // localStorage unavailable (private mode / disabled) - still close it
      // for this session, it just won't be remembered across reloads.
    }
    onDismiss(true)
  }

  if (!usage) return null

  // Once usage has actually crossed 100% (not just approaching the alert
  // threshold), the messaging switches from a heads-up to a hard warning
  // about ongoing/upcoming processing impact, with red instead of amber
  // styling to match the severity.
  const isExceeded = usage.percentage >= 100

  const theme = isExceeded
    ? {
        border: 'border-red-300 dark:border-red-700',
        bg: 'from-red-50 to-rose-50 dark:from-red-950/50 dark:to-rose-950/40',
        iconBg: 'bg-red-100 dark:bg-red-900/40',
        iconColor: 'text-red-600 dark:text-red-400',
        title: 'text-red-900 dark:text-red-200',
        text: 'text-red-800 dark:text-red-300',
        button: 'bg-red-600 hover:bg-red-700',
        track: 'bg-red-200/60 dark:bg-red-900/40',
        bar: 'bg-red-600 dark:bg-red-500',
        icon: 'error',
      }
    : {
        border: 'border-amber-300 dark:border-amber-700',
        bg: 'from-amber-50 to-yellow-50 dark:from-amber-950/50 dark:to-yellow-950/40',
        iconBg: 'bg-amber-100 dark:bg-amber-900/40',
        iconColor: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-900 dark:text-amber-200',
        text: 'text-amber-800 dark:text-amber-300',
        button: 'bg-amber-500 hover:bg-amber-600',
        track: 'bg-amber-200/60 dark:bg-amber-900/40',
        bar: 'bg-amber-500 dark:bg-amber-400',
        icon: 'warning',
      }

  return (
    <div className="fixed bottom-6 right-6 z-[10000] w-[calc(100%-3rem)] max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className={`rounded-xl border-2 ${theme.border} bg-gradient-to-br ${theme.bg} shadow-2xl overflow-hidden`}>
        <div className="p-4 flex items-start gap-3">
          <div className={`flex-shrink-0 w-9 h-9 rounded-full ${theme.iconBg} flex items-center justify-center`}>
            <span className={`material-icons ${theme.iconColor}`} style={{ fontSize: '20px' }}>{theme.icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${theme.title}`}>
              {isExceeded ? 'Franquia de eventos ultrapassada' : 'Franquia de eventos quase no limite'}
            </p>
            <p className={`text-xs ${theme.text} mt-1 leading-relaxed`}>
              {isExceeded ? (
                <>
                  Sua empresa já ultrapassou a franquia mensal de eventos, utilizando{' '}
                  <strong>{usage.percentage.toFixed(1)}%</strong> ({usage.used.toLocaleString('pt-BR')} de{' '}
                  {usage.limit.toLocaleString('pt-BR')}). O processamento de novos eventos pode ser afetado. Entre em
                  contato para ajustar seu limite.
                </>
              ) : (
                <>
                  Sua empresa já utilizou <strong>{usage.percentage.toFixed(1)}%</strong> da franquia mensal de eventos
                  ({usage.used.toLocaleString('pt-BR')} de {usage.limit.toLocaleString('pt-BR')}). Considere revisar seu
                  plano para evitar interrupções no processamento.
                </>
              )}
            </p>
          </div>
        </div>
        <div className="px-4 pb-3">
          <button
            onClick={handleAcknowledge}
            className={`w-full px-3 py-1.5 rounded-lg ${theme.button} text-white text-xs font-semibold transition`}
          >
            Estou ciente
          </button>
        </div>
        <div className={`h-1 ${theme.track}`}>
          <div
            className={`h-full ${theme.bar}`}
            style={{ width: `${remainingPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default FranchiseUsageAlert
